import React, { useMemo, useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import {
  FaBullseye,
  FaSearch,
  FaBullhorn,
  FaCheckCircle,
  FaRobot,
  FaShareAlt,
  FaEnvelope,
  FaChartLine,
  FaLightbulb,
  FaStar,
  FaHandshake,
} from 'react-icons/fa';
import bgImage from '../assets/mobapp.jpg';

/**
 * Digital Marketing page styled and animated like CyberSecurityPage
 * - semantic structure, reduced-motion support, and responsive typography
 */

const gradientText = {
  background: 'linear-gradient(to right, #f59e0b, #ef4444)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const wrapStyle = (imageUrl) => ({
  minHeight: '100vh',
  padding: '80px 20px',
  color: '#fff',
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  backgroundAttachment: 'fixed',
  backgroundImage: `linear-gradient(rgba(15,23,42,0.92), rgba(2,6,23,0.95)), url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1200px',
  margin: '0 auto',
};

const cardStyleBase = {
  borderRadius: '16px',
  padding: '30px',
  color: '#fff',
  textAlign: 'left',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
};

const iconWrapStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  marginBottom: '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
};

const sectionShell = {
  background: 'linear-gradient(to right, rgba(30,41,59,0.8), rgba(15,23,42,0.9))',
  borderRadius: '24px',
  padding: '50px',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
  maxWidth: 'min(1000px, 90vw)',
  width: '100%',
  margin: '80px auto 0',
};

const listGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
  gap: '20px',
  maxWidth: '900px',
  margin: '0 auto',
  padding: 0,
  listStyle: 'none',
};

const DigitalMarketing = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const prefersReducedMotion = useReducedMotion();

  const services = useMemo(
    () => [
      {
        icon: <FaBullseye aria-hidden="true" />,
        title: 'Lead Generation',
        description:
          'Targeted campaigns that bring high‑quality leads aligned to your goals. Focus on offers that capture attention and convert into long‑term relationships.',
        color: 'linear-gradient(135deg,#ef4444,#f59e0b)',
      },
      {
        icon: <FaSearch aria-hidden="true" />,
        title: 'SEO & Content Marketing',
        description:
          'Optimize for people and search engines. Structured site content, blogs, and resources that build authority and organic discovery over time.',
        color: 'linear-gradient(135deg,#10b981,#14b8a6)',
      },
      {
        icon: <FaBullhorn aria-hidden="true" />,
        title: 'Paid Advertising',
        description:
          'Performance‑driven ads with precise targeting and compelling creative. Continual iteration to maximize reach, ROI, and outcomes.',
        color: 'linear-gradient(135deg,#3b82f6,#0ea5e9)',
      },
      {
        icon: <FaCheckCircle aria-hidden="true" />,
        title: 'Conversion Optimization',
        description:
          'Refine funnels and UX to reduce friction and lift CVR. Improve journeys using behavioral insights and testing.',
        color: 'linear-gradient(135deg,#8b5cf6,#d946ef)',
      },
      {
        icon: <FaRobot aria-hidden="true" />,
        title: 'Marketing Automation',
        description:
          'Automated nurture across the buyer journey for timely, consistent engagement. Save time while strengthening relationships.',
        color: 'linear-gradient(135deg,rgb(0,222,247),rgb(154,0,237))',
      },
      {
        icon: <FaShareAlt aria-hidden="true" />,
        title: 'Social Media Marketing',
        description:
          'Build communities with engaging content and a consistent presence. Emphasize storytelling and meaningful interactions.',
        color: 'linear-gradient(135deg,rgb(0,140,205),rgb(75,58,127))',
      },
      {
        icon: <FaEnvelope aria-hidden="true" />,
        title: 'Email Marketing',
        description:
          'Lifecycle emails that speak to needs and intent—from welcome to loyalty. Clarity, relevance, and purposeful actions.',
        color: 'linear-gradient(135deg,#111827,#6b7280)',
      },
      {
        icon: <FaChartLine aria-hidden="true" />,
        title: 'Analytics & Reporting',
        description:
          'Clear performance insights and trend tracking to guide decisions. Surface strengths, gaps, and new opportunities.',
        color: 'linear-gradient(135deg,#0ea5e9,#06b6d4)',
      },
      {
        icon: <FaLightbulb aria-hidden="true" />,
        title: 'Brand Strategy & Positioning',
        description:
          'Define voice, vision, and competitive space. Ensure every message aligns to long‑term goals for consistent brand trust.',
        color: 'linear-gradient(135deg,#f97316,#ef4444)',
      },
      {
        icon: <FaStar aria-hidden="true" />,
        title: 'Reputation Management',
        description:
          'Monitor, build, and protect your reputation across platforms. Proactive responses and positive visibility to build trust.',
        color: 'linear-gradient(135deg,#10b981,#16a34a)',
      },
      {
        icon: <FaHandshake aria-hidden="true" />,
        title: 'Influencer & Partnership Marketing',
        description:
          'Extend reach through aligned partners and trusted voices. Authentic collaborations that open long‑term growth avenues.',
        color: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
      },
    ],
    []
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: prefersReducedMotion ? 0 : 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { type: 'spring', stiffness: 100, damping: 10 },
    },
  };

  return (
    <main style={wrapStyle(bgImage)} ref={ref} aria-label="Digital Marketing Services Page">
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <header style={{ textAlign: 'center' }}>
          <motion.h1
            style={{
              ...gradientText,
               background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
              fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
              fontWeight: 700,
              marginBottom: 24,
            }}
            variants={itemVariants}
          >
            Digital Marketing Services
          </motion.h1>

          <motion.p
            style={{
              fontSize: 'clamp(1rem, 0.5vw + 0.9rem, 1.25rem)',
              maxWidth: 900,
              margin: '0 auto 60px',
              color: '#d1d5db',
              lineHeight: 1.7,
            }}
            variants={itemVariants}
          >
            We design strategies that attract the right audience, build stronger connections, and convert interest into measurable results. Combining creativity with data‑driven insights, every campaign is focused on growth—bringing together brand visibility, engagement, and sales in one consistent flow. With a holistic approach, we help you establish and maintain a competitive edge.
          </motion.p>
        </header>

        <section aria-labelledby="services-heading">
          <h2 id="services-heading" style={{ position: 'absolute', left: -10000 }}>
            Our Services
          </h2>

          <motion.div style={gridStyle} variants={containerVariants}>
            {services.map((s) => (
              <motion.article
                key={s.title}
                aria-label={s.title}
                style={{ ...cardStyleBase, background: s.color }}
                variants={itemVariants}
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : { y: -8, scale: 1.03, boxShadow: '0 15px 30px rgba(0,0,0,0.4)' }
                }
              >
                <div style={iconWrapStyle} aria-hidden="true">{s.icon}</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 15 }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: '1rem', color: '#e5e7eb', lineHeight: 1.6 }}>{s.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        <section aria-labelledby="why-heading">
          <motion.div style={sectionShell} variants={itemVariants}>
            <h2
              id="why-heading"
              style={{
                ...gradientText,
                background: 'linear-gradient(to right, #8b5cf6, #0ea5e9)',
                fontSize: 'clamp(1.75rem, 1.5vw + 1rem, 2.5rem)',
                fontWeight: 700,
                marginBottom: 24,
                textAlign: 'center',
              }}
            >
              Why Our Marketing Works
            </h2>

            <p
              style={{
                fontSize: 'clamp(1rem, 0.4vw + 0.85rem, 1.2rem)',
                color: '#d1d5db',
                maxWidth: 800,
                margin: '0 auto 40px',
                lineHeight: 1.7,
                textAlign: 'center',
              }}
            >
              We align creativity with analytics, automate repeatable processes, and iterate based on evidence—not hunches—so your brand compounds momentum across channels.
            </p>

            <ul style={listGrid}>
              {[
                'Holistic, full‑funnel approach across brand, demand, and revenue',
                'Consistent measurement with clear reporting and actionable insights',
                'Automation that nurtures buyers at every stage without extra workload',
                'Conversion‑focused UX and messaging grounded in user behavior',
                'Brand voice and positioning that stays consistent across channels',
                'Proactive reputation monitoring to maintain credibility',
                'Authentic partnerships and influencer activations to extend reach',
              ].map((item, index) => (
                <motion.li
                  key={item}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: 16,
                    background: 'rgba(30,41,59,0.5)',
                    borderRadius: 12,
                       borderLeft: '4px solid #8b5cf6',
                  }}
                  initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    transition: prefersReducedMotion
                      ? { duration: 0 }
                      : { delay: 0.4 + index * 0.08 },
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      marginRight: 12,
                      color: '#8b5cf6',
                      fontSize: '1.5rem',
                    }}
                  >
                    •
                  </span>
                  <span style={{ color: '#e5e7eb', lineHeight: 1.6 }}>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </section>

        <section aria-labelledby="contact-heading" style={{ marginTop: 64 }}>
          <h2 id="contact-heading" style={{ position: 'absolute', left: -10000 }}>
            Contact Us
          </h2>
          <ContactForm />
        </section>
      </motion.div>
    </main>
  );
};

export default DigitalMarketing;
